import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Builder } from '@builder.io/react';

export const TotalImpactSlider = ({
  totalImpact = '',
  impactItems = [],
  impactLogo,
  bgColor = '#E1EFF2',
  title = {
    en: 'Total Impact:',
    fr: 'IMPACT TOTAL:',
    ar: 'التأثير الكلي:',
  },
  mobileSpeed = 20,
  desktopSpeed = 30,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [copyWidth, setCopyWidth] = useState(0);
  const [copies, setCopies] = useState(2);

  useEffect(() => {
    const updateSizes = () => {
      if (!measureRef.current || !containerRef.current) return;
      const thisWidth = measureRef.current.offsetWidth;
      const containerWidth = containerRef.current.offsetWidth;
      if (thisWidth > 0) {
        setCopyWidth(thisWidth);
        setCopies(Math.ceil(containerWidth / thisWidth) + 1);
      }
    };
    updateSizes();
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
  }, [impactItems]);

  if (!impactItems.length) return null;

  const allItems = [];
  for (let i = 0; i < copies; i++) {
    allItems.push(...impactItems);
  }

  return (
    <section
      className="py-6 md:py-8 flex flex-col items-center"
      style={{ backgroundColor: bgColor }}
    >
      {impactLogo && (
        <div className="flex items-center justify-center mb-4 md:mb-5">
          <img src={impactLogo} alt="Impact Logo" />
        </div>
      )}

      {(title || totalImpact) && (
        <div className="flex flex-col justify-center mb-8 sm:mb-4 md:mb-10 px-1 md:px-2">
          <h2
            className={`text-[18px] md:text-4xl ${
              lang === 'ar' ? 'font-[AlmariMedium] font-bold' : 'font-brandingBold'
            } text-[#253B7E] text-center font-black`}
          >
            {title && (
              <span className="font-normal text-[18px] md:text-4xl text-[#78716C] uppercase mr-1">
                {title[lang] || title.en}
              </span>
            )}
            {totalImpact && <>&nbsp;{totalImpact}</>}
          </h2>
        </div>
      )}

      <div ref={containerRef} className="w-full max-w-screen-2xl overflow-hidden relative">
        {/* hidden measurer */}
        <div
          ref={measureRef}
          className="absolute top-0 left-0 flex items-center whitespace-nowrap opacity-0 pointer-events-none"
        >
          {impactItems.map((item, i) => (
            <ImpactCard key={i} item={item} lang={lang} />
          ))}
        </div>

        {/* the scrolling track */}
        <div className="flex items-center whitespace-nowrap animate-marquee" style={{ '--copy-width': `${copyWidth}px` }}>
          {allItems.map((item, idx) => (
            <ImpactCard key={idx} item={item} lang={lang} />
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-1 * var(--copy-width))); }
        }

        @media (max-width: 768px) {
          .animate-marquee {
            animation: marquee ${mobileSpeed}s linear infinite;
          }
        }

        @media (min-width: 769px) {
          .animate-marquee {
            animation: marquee ${desktopSpeed}s linear infinite;
          }
        }
      `}</style>
    </section>
  );
};

const ImpactCard = ({ item, lang }) => {
  const { img, value, title, frenchTitle, arabicTitle } = item;
  if (!img?.src || !value) return null;
  return (
    <div className="flex flex-col items-center justify-center min-w-[160px] md:mx-8">
      <img src={img.src} alt={img.alt || ''} className="h-16 md:h-20 w-auto" />
      <div className="flex flex-col items-center text-center">
        <h4
          className={`md:text-2xl ${
            lang === 'ar' ? 'font-[AlmariMedium] font-bold' : 'font-brandingBold'
          } text-[#253B7E]`}
        >
          {value}
        </h4>
        <small
          className={`text-xs md:text-base ${
            lang === 'ar' ? 'font-[AlmariMedium]' : 'font-brandingMedium'
          } uppercase text-[#78716C]`}
        >
          {lang === 'fr'
            ? frenchTitle
            : lang === 'ar'
            ? arabicTitle
            : title}
        </small>
      </div>
    </div>
  );
};

Builder.registerComponent(TotalImpactSlider, {
  name: 'Total Impact Slider',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: '#E1EFF2',
    },
    {
      name: 'impactLogo',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg'],
      required: false,
    },
    {
      name: 'totalImpact',
      type: 'string',
      helperText: 'Total impact figures if you want to show them in the title',
      required: false,
    },
    {
      name: 'title',
      type: 'object',
      defaultValue: {
        en: 'Total Impact:',
        fr: 'IMPACT TOTAL:',
        ar: 'التأثير الكلي:',
      },
      subFields: [
        { name: 'en', type: 'string', required: false },
        { name: 'fr', type: 'string', required: false },
        { name: 'ar', type: 'string', required: false },
      ],
      required: false,
    },
    {
      name: 'impactItems',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'img',
          type: 'object',
          subFields: [
            {
              name: 'src',
              type: 'file',
              allowedFileTypes: ['jpeg', 'png', 'svg'],
              required: true,
            },
            { name: 'alt', type: 'string', required: false },
          ],
          required: true,
        },
        { name: 'value', type: 'string', required: true },
        { name: 'title', type: 'string', required: false },
        { name: 'frenchTitle', type: 'string', required: false },
        { name: 'arabicTitle', type: 'string', required: false },
      ],
      required: false,
    },
    {
      name: 'mobileSpeed',
      type: 'number',
      defaultValue: 20,
      helperText: 'Seconds for one full loop on mobile screens',
    },
    {
      name: 'desktopSpeed',
      type: 'number',
      defaultValue: 30,
      helperText: 'Seconds for one full loop on desktop screens',
    },
  ],
});
